<script setup>
import { h, ref, watch, computed } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import WithLabel from 'v3/components/Form/WithLabel.vue';
import Editor from 'next/Editor/Editor.vue';
import Switch from 'next/switch/Switch.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import DurationInput from 'next/input/DurationInput.vue';
import SingleSelect from 'dashboard/components-next/filter/inputs/SingleSelect.vue';
import Input from 'next/input/Input.vue';
import { DURATION_UNITS } from 'dashboard/components-next/input/constants';

const { t } = useI18n();
const duration = ref(0);
const unit = ref(DURATION_UNITS.MINUTES);
const message = ref('');
const reminderMessage = ref('');
const closingDuration = ref(1);
const closingUnit = ref(DURATION_UNITS.MINUTES);
const closingEnabled = ref(true);
const aiEnabled = ref(false);
const aiEndpoint = ref('');
const MAX_DURATION_MINUTES = 1_439_856;
const DELAY_TOKEN = '{{jeda_penutupan}}';
const MINUTES_PER_UNIT = {
  [DURATION_UNITS.MINUTES]: 1,
  [DURATION_UNITS.HOURS]: 60,
  [DURATION_UNITS.DAYS]: 1440,
};
const labelToApply = ref({});
const ignoreWaiting = ref(false);
const isEnabled = ref(false);
const isSubmitting = ref(false);

const { currentAccount, updateAccount } = useAccount();

watch(unit, value => {
  duration.value = Math.max(duration.value || 10, 10, MINUTES_PER_UNIT[value]);
});
watch(closingUnit, value => {
  closingDuration.value = Math.max(
    closingDuration.value || 1,
    MINUTES_PER_UNIT[value]
  );
});

const closingHelp = computed(() =>
  closingEnabled.value
    ? t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.CLOSING.HELP')
    : t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.CLOSING.DISABLED_HELP')
);

watch(closingEnabled, enabled => {
  const withClosing = t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.REMINDER.DEFAULT', {
    delayToken: DELAY_TOKEN,
  });
  const reminderOnly = t(
    'GENERAL_SETTINGS.FORM.AUTO_RESOLVE.REMINDER.ONLY_DEFAULT'
  );
  if (!enabled && reminderMessage.value === withClosing)
    reminderMessage.value = reminderOnly;
  if (enabled && reminderMessage.value === reminderOnly)
    reminderMessage.value = withClosing;
});

const labels = useMapGetter('labels/getLabels');

const labelOptions = computed(() =>
  labels.value?.length
    ? labels.value.map(label => ({
        id: label.title,
        name: label.title,
        icon: h('span', {
          class: `size-[12px] ring-1 ring-n-alpha-1 dark:ring-white/20 ring-inset rounded-sm`,
          style: { backgroundColor: label.color },
        }),
      }))
    : []
);

const selectedLabelName = computed(() => {
  return labelToApply.value?.name ?? null;
});

watch(
  [currentAccount, labelOptions],
  () => {
    const {
      auto_resolve_after,
      auto_resolve_message,
      auto_resolve_ignore_waiting,
      auto_resolve_label,
    } = currentAccount.value?.settings || {};

    duration.value = auto_resolve_after || 10;
    message.value =
      auto_resolve_message ||
      t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.CLOSING.DEFAULT');
    const settings = currentAccount.value?.settings || {};
    reminderMessage.value =
      settings.auto_resolve_warning_message ||
      (settings.auto_resolve_closing_enabled === false
        ? t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.REMINDER.ONLY_DEFAULT')
        : t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.REMINDER.DEFAULT', {
            delayToken: DELAY_TOKEN,
          }));
    closingDuration.value = settings.auto_resolve_warning_minutes || 1;
    closingEnabled.value = settings.auto_resolve_closing_enabled !== false;
    aiEnabled.value = settings.auto_resolve_ai_enabled === true;
    aiEndpoint.value = settings.auto_resolve_ai_endpoint || '';
    closingUnit.value = DURATION_UNITS.MINUTES;
    if (closingDuration.value % 1440 === 0) {
      closingUnit.value = DURATION_UNITS.DAYS;
    } else if (closingDuration.value % 60 === 0) {
      closingUnit.value = DURATION_UNITS.HOURS;
    }
    ignoreWaiting.value = auto_resolve_ignore_waiting;
    // find the correct label option from the list
    // the single select component expects the full label object
    // in our case, the label id and name are both the same
    labelToApply.value =
      labelOptions.value.find(option => option.name === auto_resolve_label) ||
      {};

    // Set unit based on duration and its divisibility
    if (duration.value) {
      if (duration.value % (24 * 60) === 0) {
        unit.value = DURATION_UNITS.DAYS;
      } else if (duration.value % 60 === 0) {
        unit.value = DURATION_UNITS.HOURS;
      } else {
        unit.value = DURATION_UNITS.MINUTES;
      }
    }

    isEnabled.value = Boolean(auto_resolve_after);
  },
  { deep: true, immediate: true }
);

const updateAccountSettings = async settings => {
  try {
    isSubmitting.value = true;
    await updateAccount(settings, { silent: true });
    useAlert(t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.DURATION.API.SUCCESS'));
  } catch (error) {
    useAlert(t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.DURATION.API.ERROR'));
  } finally {
    isSubmitting.value = false;
  }
};

const handleSubmit = async () => {
  if (
    !Number.isInteger(duration.value) ||
    duration.value < 10 ||
    duration.value > MAX_DURATION_MINUTES
  ) {
    useAlert(t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.DURATION.ERROR'));
    return Promise.resolve();
  }

  if (
    !Number.isInteger(closingDuration.value) ||
    closingDuration.value < 1 ||
    closingDuration.value > MAX_DURATION_MINUTES
  ) {
    useAlert(t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.CLOSING.ERROR'));
    return Promise.resolve();
  }
  if (aiEndpoint.value.trim()) {
    try {
      const endpoint = new URL(aiEndpoint.value.trim());
      if (
        endpoint.protocol !== 'https:' ||
        endpoint.username ||
        endpoint.password
      )
        throw new Error('Invalid endpoint');
    } catch {
      useAlert(t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.AI.ENDPOINT_ERROR'));
      return Promise.resolve();
    }
  }

  return updateAccountSettings({
    auto_resolve_warning_minutes: closingDuration.value,
    auto_resolve_warning_message: reminderMessage.value,
    auto_resolve_closing_enabled: closingEnabled.value,
    auto_resolve_ai_enabled: aiEnabled.value,
    auto_resolve_ai_endpoint: aiEndpoint.value.trim(),
    auto_resolve_after: duration.value,
    auto_resolve_message: message.value,
    auto_resolve_ignore_waiting: ignoreWaiting.value,
    auto_resolve_label: selectedLabelName.value,
  });
};

const handleDisable = async () =>
  updateAccountSettings({ auto_resolve_after: null });

const toggleAutoResolve = async () => {
  if (!isEnabled.value) handleDisable();
};
</script>

<template>
  <div
    class="flex flex-col w-full outline-1 outline outline-n-container rounded-xl bg-n-solid-2 divide-y divide-n-weak"
  >
    <div class="flex flex-col gap-2 items-start px-5 py-4">
      <div class="flex justify-between items-center w-full">
        <h3 class="text-heading-2 text-n-slate-12">
          {{ t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.TITLE') }}
        </h3>
        <div class="flex justify-end">
          <Switch v-model="isEnabled" @change="toggleAutoResolve" />
        </div>
      </div>
      <p class="mb-0 text-body-para text-n-slate-11">
        {{ t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.NOTE') }}
      </p>
    </div>

    <div v-if="isEnabled" class="px-5 py-4">
      <form class="grid gap-5" @submit.prevent="handleSubmit">
        <WithLabel
          :label="t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.DURATION.LABEL')"
          :help-message="t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.DURATION.HELP')"
        >
          <div class="gap-2 w-full grid grid-cols-[3fr_1fr]">
            <!-- allow 10 mins to 999 days -->
            <DurationInput
              v-model="duration"
              v-model:unit="unit"
              :min="10"
              :max="MAX_DURATION_MINUTES"
              class="w-full"
            />
          </div>
        </WithLabel>
        <WithLabel
          :label="t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.REMINDER.LABEL')"
          :help-message="
            t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.REMINDER.HELP', {
              delayToken: DELAY_TOKEN,
            })
          "
        >
          <Editor
            v-model="reminderMessage"
            class="w-full"
            channel-type="Context::NoToolbar"
            :enable-canned-responses="false"
            :show-character-count="false"
          />
        </WithLabel>
        <div class="space-y-1">
          <div class="flex justify-between items-center gap-4">
            <label class="text-sm font-medium leading-6 text-n-slate-12">
              {{ t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.CLOSING.LABEL') }}
            </label>
            <Switch
              v-model="closingEnabled"
              data-testid="closing-toggle"
              :aria-label="
                t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.CLOSING.LABEL')
              "
            />
          </div>
          <div class="gap-2 w-full grid grid-cols-[3fr_1fr]">
            <DurationInput
              v-model="closingDuration"
              v-model:unit="closingUnit"
              :min="1"
              :max="MAX_DURATION_MINUTES"
              :disabled="!closingEnabled"
            />
          </div>
          <p class="mb-0 text-sm mt-1.5 ml-px text-n-slate-10 leading-tight">
            {{ closingHelp }}
          </p>
        </div>
        <WithLabel
          :label="t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.MESSAGE.LABEL')"
          :help-message="t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.MESSAGE.HELP')"
        >
          <Editor
            v-model="message"
            :disabled="!closingEnabled"
            class="w-full"
            channel-type="Context::NoToolbar"
            enable-variables
            :enable-canned-responses="false"
            :show-character-count="false"
            :placeholder="
              t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.MESSAGE.PLACEHOLDER')
            "
          />
        </WithLabel>
        <WithLabel :label="t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.PREFERENCES')">
          <div
            class="rounded-xl border border-n-weak bg-n-solid-1 w-full text-sm text-n-slate-12 divide-y divide-n-weak"
          >
            <div class="p-3 h-12 flex items-center justify-between">
              <span>
                {{
                  t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.IGNORE_WAITING.LABEL')
                }}
              </span>
              <Switch v-model="ignoreWaiting" />
            </div>
            <div class="p-3 h-12 flex items-center justify-between">
              <span>
                {{ t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.LABEL.LABEL') }}
              </span>
              <SingleSelect
                v-model="labelToApply"
                :options="labelOptions"
                :placeholder="
                  $t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.LABEL.PLACEHOLDER')
                "
                placeholder-icon="i-lucide-chevron-down"
                placeholder-trailing-icon
                variant="faded"
              />
            </div>
          </div>
        </WithLabel>
        <div
          data-testid="kai-settings"
          class="rounded-xl border border-n-weak bg-n-solid-1 p-3 flex flex-col gap-4"
        >
          <div>
            <label
              class="flex items-center gap-2 text-sm text-n-slate-12 cursor-pointer"
            >
              <input
                v-model="aiEnabled"
                type="checkbox"
                class="w-4 h-4 accent-n-brand"
              />
              <span>{{
                t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.AI.LABEL')
              }}</span>
            </label>
            <p class="mb-0 ms-6 mt-1.5 text-sm text-n-slate-10">
              {{ t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.AI.HELP') }}
            </p>
          </div>
          <WithLabel
            :label="t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.AI.ENDPOINT_LABEL')"
            :help-message="
              t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.AI.ENDPOINT_HELP')
            "
          >
            <Input
              v-model="aiEndpoint"
              class="w-full"
              type="url"
              :disabled="!aiEnabled"
              :placeholder="
                t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.AI.ENDPOINT_PLACEHOLDER')
              "
              autocomplete="off"
            />
          </WithLabel>
        </div>
        <div class="flex gap-2">
          <NextButton
            blue
            type="submit"
            :is-loading="isSubmitting"
            :label="t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.UPDATE_BUTTON')"
          />
        </div>
      </form>
    </div>
  </div>
</template>
